import { failingFetcher } from '@lib/http/fetch';
import { Organization } from '@lib/types/api/Organization';
import { User } from '@lib/types/common/User';
import {
  readActiveOrgCookie,
  writeActiveOrgCookie,
} from '@lib/util/activeOrgCookie';
import { resolveActiveOrg } from '@lib/util/resolveActiveOrg';
import { useSession } from 'next-auth/react';
import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useSWR from 'swr';

export const OrganizationsContext = createContext<{
  organizations: Organization[];
  activeOrganization: Organization | null;
  handleOrgChange: (
    organization: Organization,
    suppressReload?: boolean
  ) => void;
  errorFetchingOrganizations: any;
}>({
  organizations: [],
  activeOrganization: null,
  handleOrgChange: () => {},
  errorFetchingOrganizations: null,
});

export const OrganizationsProvider = ({
  user,
  children,
}: PropsWithChildren<{ user?: User }>) => {
  const { data: session } = useSession();
  const genesisUser = session?.accessToken?.genesis_user;
  const userId = typeof genesisUser === 'string' ? genesisUser : undefined;
  const { data, error } = useSWR<{
    organizations: Organization[];
    defaultOrganizationId: string;
  }>(
    // only fetch if there is a user logged in
    user && userId ? ['/api/users/organizations', userId] : null,
    ([url]) => failingFetcher(url)
  );

  const { organizations, defaultOrganizationId } = useMemo(() => {
    return {
      organizations: data?.organizations ? data.organizations : [],
      defaultOrganizationId: data?.defaultOrganizationId,
    };
  }, [data]);

  const [activeOrganization, setActiveOrganization] =
    useState<Organization | null>(null);

  // Self-heals: when the resolved org differs from the cookie (stale or
  // missing), handleOrgChange rewrites the cookie.
  useEffect(() => {
    const storedOrgId = readActiveOrgCookie();
    const activeOrg = resolveActiveOrg(
      storedOrgId,
      organizations,
      defaultOrganizationId
    );

    if (!activeOrg) {
      setActiveOrganization(null);
      return;
    }
    if (activeOrg.id === storedOrgId) {
      setActiveOrganization(activeOrg);
    } else {
      handleOrgChange(activeOrg, false);
    }
  }, [organizations, defaultOrganizationId]);

  /**
   * Handles the change of the active organization.
   *
   * Sets the provided organization as the active organization, updates the corresponding cookie,
   * and optionally reloads the page.
   *
   * @param org - The organization to set as active.
   * @param shouldReload - Whether to reload the page after changing the organization. Defaults to true.
   */
  const handleOrgChange = (org: Organization, shouldReload = true) => {
    setActiveOrganization(org);
    writeActiveOrgCookie(org.id);
    if (shouldReload) {
      window.location.reload();
    }
  };

  const value = useMemo(() => {
    return {
      organizations,
      errorFetchingOrganizations: error,
      activeOrganization,
      handleOrgChange,
    };
  }, [activeOrganization, organizations, error]);

  return (
    <OrganizationsContext.Provider value={value}>
      {children}
    </OrganizationsContext.Provider>
  );
};

export const useOrganizations = () => {
  return useContext(OrganizationsContext);
};
